import AutoSuggest, { type AutoSuggestInputProps, type SelectedSuggestion } from '@rio-cloud/rio-uikit/AutoSuggest';
import classNames from 'classnames';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { type Control, useController } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { type ApiCustomerPoi, useGetCustomerPoisQuery } from '#api/poisApi.slice.ts';
import { LocationSuggestionItem } from '#components/order/CreateOrderDialog/LocationSuggestionItem.tsx';
import { mapCountryCodeISO3ToISO2 } from '#utils/country.util.ts';
import { fetchHereGeocodeLocation, type HereGeocodeLocation } from '#utils/here/fetchHereGeocodePosition.ts';
import { fetchHereLocationSuggestions } from '#utils/here/fetchHereLocationSuggestions.ts';

import {
    getLocationSuggestionAddressesHeader,
    getLocationSuggestionPoiHeader,
    type LocationSuggestion,
    toAddress,
} from './LocationSuggestion.ts';
import type { Location } from './OrderFormValidation.ts';
import { type SavePoiCreateParams, SavePoiDialogLink } from './SavePoiDialogLink.tsx';

const normalizePoiSearchText = (value: string) => value.trim().toLowerCase();

const getPoiSearchTokens = (query: string) => normalizePoiSearchText(query).split(/\s+/).filter(Boolean);

export const poiMatchesSearch = (poi: ApiCustomerPoi, query: string) => {
    const tokens = getPoiSearchTokens(query);
    if (tokens.length === 0) {
        return true;
    }

    const searchableText = normalizePoiSearchText(`${poi.name} ${poi.address ?? ''}`);
    return tokens.every(token => searchableText.includes(token));
};

export const getSavePoiCreateParams = (
    suggestion: Pick<LocationSuggestion, 'addressPreview' | 'latitude' | 'longitude'>,
    companyName: unknown
): SavePoiCreateParams => ({
    lat: suggestion.latitude,
    lng: suggestion.longitude,
    label: suggestion.addressPreview.trim() || String(companyName ?? '').trim(),
});

const geocodeLocationToAddressSuggestion = (
    poi: Pick<ApiCustomerPoi, 'id' | 'name' | 'address' | 'category' | 'coordinates'>,
    geocodeLocation: HereGeocodeLocation | undefined
): LocationSuggestion => ({
    key: `poi-${poi.id}`,
    type: 'poi',
    name: poi.name,
    category: poi.category,
    addressPreview: poi.address ?? '',
    city: geocodeLocation?.address?.city,
    country: mapCountryCodeISO3ToISO2(geocodeLocation?.address?.countryCode),
    street: [geocodeLocation?.address?.street, geocodeLocation?.address?.houseNumber].filter(Boolean).join(' ').trim(),
    postcode: geocodeLocation?.address?.postalCode,
    latitude: poi.coordinates.latitude,
    longitude: poi.coordinates.longitude,
});

type ControlledAddressAutocompleteProps = {
    // biome-ignore lint/suspicious/noExplicitAny: fix later when cleanup
    control: Control<any>;
    name: string;
    companyName: string;
    triggerPositionUpdate: (location: Location) => Promise<void>;
    formGroupClassName?: string;
};

export const ControlledAddressAutocomplete = forwardRef<HTMLInputElement, ControlledAddressAutocompleteProps>(
    ({ control, name, companyName, triggerPositionUpdate, formGroupClassName }, _ref) => {
        const { field } = useController({ control, name });
        const { field: companyField } = useController({ control, name: companyName });
        const [value, setValue] = useState('');
        const [savePoiMode, setSavePoiMode] = useState<'create' | 'manage'>('create');
        const [savePoiCreateParams, setSavePoiCreateParams] = useState<SavePoiCreateParams | undefined>();
        const [hereSuggestions, setHereSuggestions] = useState<LocationSuggestion[]>([]);
        const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
        const intl = useIntl();
        const { data: customerPois = [], isFetching: isFetchingCustomerPois } = useGetCustomerPoisQuery();

        useEffect(() => {
            return () => {
                if (debounceTimeout.current) {
                    clearTimeout(debounceTimeout.current);
                }
            };
        }, []);

        const filteredPoiSuggestions = useMemo(() => {
            const query = value.trim().toLowerCase();
            return customerPois
                .filter(poi => poiMatchesSearch(poi, query))
                .map<LocationSuggestion>(poi => ({
                    key: `poi-${poi.id}`,
                    type: 'poi',
                    name: poi.name,
                    category: poi.category,
                    addressPreview: poi.address ?? '',
                    latitude: poi.coordinates.latitude,
                    longitude: poi.coordinates.longitude,
                }));
        }, [customerPois, value]);

        const suggestions = useMemo<LocationSuggestion[]>(() => {
            const poiHeader: LocationSuggestion[] = filteredPoiSuggestions.length
                ? [getLocationSuggestionPoiHeader(intl)]
                : [];
            const addressHeader: LocationSuggestion[] = hereSuggestions.length
                ? [getLocationSuggestionAddressesHeader(intl)]
                : [];

            return [...poiHeader, ...filteredPoiSuggestions, ...addressHeader, ...hereSuggestions];
        }, [filteredPoiSuggestions, hereSuggestions, intl]);

        const renderSuggestion = (suggestion: LocationSuggestion) => <LocationSuggestionItem suggestion={suggestion} />;

        const handleSuggestionsFetchRequested = ({ value }: { value: string }) => {
            setValue(value);
            const query = value.trim();
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }

            debounceTimeout.current = setTimeout(async () => {
                if (query.length < 3) {
                    setHereSuggestions([]);
                    return;
                }

                const results = await fetchHereLocationSuggestions(query);
                const suggestions = results
                    .filter(
                        result => !!result.address?.street && !!result.address?.postalCode && !!result.address?.city
                    )
                    .map<LocationSuggestion>((result, index) => ({
                        key: `here-${result.id ?? crypto.randomUUID()}-${index}`,
                        type: 'here',
                        name: result.title,
                        label: result.address?.label,
                        addressPreview: result.address?.label ?? result.title ?? '',
                        city: result.address?.city,
                        country: mapCountryCodeISO3ToISO2(result.address?.countryCode),
                        street: [result.address?.street, result.address?.houseNumber].filter(Boolean).join(' ').trim(),
                        postcode: result.address?.postalCode,
                        latitude: result.position.lat,
                        longitude: result.position.lng,
                    }));
                setHereSuggestions(suggestions);
            }, 300);
        };

        const inputProps: AutoSuggestInputProps = {
            icon: 'rioglyph rioglyph-map-marker',
            value,
        };

        const handlePoiSuggestionSelected = async (found: LocationSuggestion) => {
            setSavePoiMode('manage');
            setSavePoiCreateParams(undefined);
            companyField.onChange(found.name ?? '');
            const geocodeLocation = await fetchHereGeocodeLocation(found.addressPreview);
            const location = toAddress(
                geocodeLocationToAddressSuggestion(
                    {
                        id: found.key,
                        name: found.name ?? '',
                        address: found.addressPreview,
                        category: found.category,
                        coordinates: {
                            latitude: found.latitude,
                            longitude: found.longitude,
                        },
                    },
                    geocodeLocation
                )
            );
            field.onChange(location);
            triggerPositionUpdate(location).catch(console.error);
            setValue('');
        };

        const handleSuggestionSelected = (selected: SelectedSuggestion) => {
            const found = suggestions.find(s => s.key === selected.suggestion.key);
            if (!found || found.type === 'header') {
                setValue('');
                return;
            }

            if (found.type === 'poi') {
                handlePoiSuggestionSelected(found).catch(console.error);
                return;
            }

            setSavePoiMode('create');
            setSavePoiCreateParams(getSavePoiCreateParams(found, companyField.value));
            // When the name is different to the address label it is probably a company name
            companyField.onChange(found.label !== found.name ? found.name : '');
            const location = toAddress(found);
            field.onChange(location);
            triggerPositionUpdate(location).catch(console.error);
            setValue('');
        };

        return (
            <div className={classNames('form-group', formGroupClassName)}>
                <div>
                    <div className='display-flex align-items-center justify-content-between gap-10 width-100pct'>
                        <label htmlFor={`${name}.addressSearch`} className='margin-bottom-0'>
                            <FormattedMessage id='intl-msg:ordercom-cloud-frontend.orders.create-dialog.location.address-search' />
                        </label>
                        <SavePoiDialogLink mode={savePoiMode} createParams={savePoiCreateParams} />
                    </div>

                    <AutoSuggest
                        id={`${name}.addressSearch`}
                        inputProps={inputProps}
                        suggestions={suggestions}
                        customSuggestionKey='key'
                        // @ts-expect-error - Types for custom render functions still expect type AutoSuggestSuggestion
                        getSuggestionValue={(suggestion: LocationSuggestion) => suggestion.addressPreview}
                        isLoading={isFetchingCustomerPois}
                        noItemMessage={
                            value.trim().length >= 3 && suggestions.length === 0
                                ? intl.formatMessage({
                                      id: 'intl-msg:ordercom-cloud-frontend.orders.address-autocomplete.address-not-found',

                                      defaultMessage: 'Address not found',
                                  })
                                : undefined
                        }
                        onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
                        // @ts-expect-error - Types for custom render functions still expect type AutoSuggestSuggestion
                        renderSuggestion={renderSuggestion}
                        openOnFocus
                        onSuggestionSelected={(_, selected) => handleSuggestionSelected(selected)}
                    />
                </div>
            </div>
        );
    }
);
